import {
    Component,
    ChangeDetectionStrategy,
    signal,
    computed,
    input,
    output,
    effect,
    untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { SelectComponent } from 'src/app/@core/components/ui/select/select.component';
import { SelectOption } from 'src/app/@core/components/ui/select/select.model';
import { MultiSelectComponent } from 'src/app/@core/components/ui/multi-select/multi-select.component';
import { MultiSelectOption } from 'src/app/@core/components/ui/multi-select/multi-select.model';
import {
    PromptAccessLevel,
    PromptAccessLevelEnum,
} from '../../../../services/prompt-library.service';

/**
 * PromptEditorDialogComponent — shared presentational dialog for creating
 * and editing prompt library entries.
 *
 * Owns form UI state (title, content, accessLevel, groupId) and delegates
 * all API logic to the parent via the `saved` output.
 *
 * Usage (create):
 * ```html
 * <app-prompt-editor-dialog
 *   [visible]="show()"
 *   dialogTitle="New Template"
 *   [groupOptions]="groupSelectOptions()"
 *   [loading]="isSaving()"
 *   (saved)="onCreate($event)"
 *   (closed)="show.set(false)" />
 * ```
 *
 * Usage (edit):
 * ```html
 * <app-prompt-editor-dialog
 *   [visible]="show()"
 *   dialogTitle="Edit Template"
 *   [initialTitle]="prompt().title"
 *   [initialContent]="prompt().content"
 *   [initialAccessLevel]="prompt().accessLevel"
 *   [initialGroupId]="prompt().groups[0] ?? null"
 *   [groupOptions]="groupSelectOptions()"
 *   [loading]="isSaving()"
 *   (saved)="onEdit($event)"
 *   (closed)="show.set(false)" />
 * ```
 */
@Component({
    selector: 'app-prompt-editor-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, ButtonComponent, InputComponent, DialogComponent, SelectComponent, MultiSelectComponent],
    templateUrl: './prompt-editor-dialog.component.html',
})
export class PromptEditorDialogComponent {
    // -----------------------------------------------------------------------
    // Inputs
    // -----------------------------------------------------------------------

    /** Controls dialog visibility. */
    visible = input<boolean>(false);

    /** Text shown in the dialog header bar. */
    dialogTitle = input<string>('Save Prompt');

    /** Label for the primary save button (e.g. 'Save', 'Save to Library'). */
    saveLabel = input<string>('Save');

    /** Pre-fill value for the Title field — set when editing an existing prompt. */
    initialTitle = input<string>('');

    /** Pre-fill value for the Content field — set when editing or saving from a message. */
    initialContent = input<string>('');

    /**
     * Pre-fill value for the Access Level field.
     * Defaults to PERSONAL — the safest default for new prompts.
     */
    initialAccessLevel = input<PromptAccessLevel>(PromptAccessLevelEnum.PERSONAL as PromptAccessLevel);

    /**
     * Available groups shown in the Group multi-select.
     * Pass an empty array to hide the group field (e.g. when used from the thread view).
     */
    groupOptions = input<MultiSelectOption[]>([]);

    /**
     * Pre-selected group IDs — set when editing a prompt that already belongs to groups,
     * or when creating inside a specific group context.
     */
    initialGroupIds = input<string[]>([]);

    /**
     * When true, a loading spinner is shown on the save button.
     * The parent controls this while the API call is in flight.
     */
    loading = input<boolean>(false);

    // -----------------------------------------------------------------------
    // Outputs
    // -----------------------------------------------------------------------

    /**
     * Emitted when the user submits a valid form.
     * Includes title, content, accessLevel and the selected groupIds array.
     * The parent is responsible for the actual API call (create or update).
     */
    saved = output<{ title: string; content: string; accessLevel: PromptAccessLevel; groupIds: string[] }>();

    /** Emitted when the dialog is cancelled or closed without saving. */
    closed = output<void>();

    // -----------------------------------------------------------------------
    // Expose enum to template
    // -----------------------------------------------------------------------
    protected readonly AccessLevel = PromptAccessLevelEnum;

    // -----------------------------------------------------------------------
    // "Share with" dropdown options — Personal (default) and Team
    // -----------------------------------------------------------------------
    readonly accessLevelOptions: SelectOption[] = [
        { label: 'Personal', value: PromptAccessLevelEnum.PERSONAL },
        { label: 'Workspace', value: PromptAccessLevelEnum.WORKSPACE },
    ];

    // -----------------------------------------------------------------------
    // Internal form state
    // -----------------------------------------------------------------------

    /** Bound to the title input field. */
    formTitle = signal('');

    /** Bound to the content textarea field. */
    formContent = signal('');

    /** Bound to the "Share with" dropdown. Defaults to PERSONAL. */
    formAccessLevel = signal<PromptAccessLevel>(PromptAccessLevelEnum.PERSONAL as PromptAccessLevel);

    /** Bound to the Group multi-select. Empty array means no groups selected. */
    formGroupIds = signal<string[]>([]);

    /** True when both required text fields have non-empty trimmed values. */
    isValid = computed(() =>
        this.formTitle().trim().length > 0 && this.formContent().trim().length > 0
    );

    // -----------------------------------------------------------------------
    // Sync internal form state from inputs when the dialog opens / re-opens.
    // signal-based inputs do not trigger ngOnChanges, so we use effect().
    // -----------------------------------------------------------------------

    private readonly _inputSync = effect(() => {
        const isVisible = this.visible();
        const title = this.initialTitle();
        const content = this.initialContent();
        const accessLevel = this.initialAccessLevel();
        const groupIds = this.initialGroupIds();
        // Only reset the form when the dialog opens to avoid clobbering the
        // user's in-progress edits while the dialog is already open.
        untracked(() => {
            if (isVisible) {
                this.formTitle.set(title);
                this.formContent.set(content);
                // Ensure the access level is always a valid API value (never 'ALL')
                this.formAccessLevel.set(
                    accessLevel === PromptAccessLevelEnum.ALL
                        ? PromptAccessLevelEnum.PERSONAL as PromptAccessLevel
                        : accessLevel
                );
                this.formGroupIds.set([...groupIds]);
            }
        });
    });

    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------

    /** Close the dialog without saving. */
    close(): void {
        this.closed.emit();
    }

    /** Emit the form data so the parent can persist it. */
    save(): void {
        if (!this.isValid()) return;
        this.saved.emit({
            title: this.formTitle().trim(),
            content: this.formContent(),
            accessLevel: this.formAccessLevel(),
            groupIds: this.formGroupIds(),
        });
    }
}
